import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import MDTypography from 'components/MDTypography';

import PapaParse from 'papaparse';
import { toast } from 'react-toastify';

import ProgressStatus from 'components/ProgressStatus/ProgressStatus';
import TableTypography from 'components/TableTypography/TableTypography';

import debounce from 'lodash/debounce';

import { downloadTransactionService } from 'services/transactions.service';
import {
  approveTransaction,
  getCancelledTransactions,
  getPendingTransactions,
  getPrimaryTransactions,
  getSecondaryTransactions,
  getWithdrawalTransactions,
  resetTransactions
} from 'store/reducers/transactionsSlice';

import { formatCurrency, formatDate, formatPercentages } from 'helpers/formatData';
import {
  cancelledRows,
  openRows,
  primaryRows,
  secondaryRows,
} from 'layouts/TransactionsManagement/components/Rows';
import {
  cancelledColumns,
  pendingColumns,
  primaryColumns,
  secondaryColumns,
  withdrawalColumns,
} from '../constants/columns.constants';

import { downloadCSV, downloadXLSX } from 'helpers/fileDownload';


const useTransactionsHook = ({ transactionType }) => {
  const dispatch = useDispatch();
  const { transactionsList } = useSelector((state) => state.transactions);

  const [isLoading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    id: 'id',
    desc: true,
  });
  const [isApproveModal, setIsApproveModal] = useState(false);
  const [userId, setUserId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [filterObj, setFilterObj] = useState({
    filterBy: '',
    filterValues: [],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionId, setTransactionId] = useState(null);

  const [isApprovedFilterValue, setIsApproveFilterValue] = useState('');
  const [isApproveLoading, setIsApproveLoading] = useState(false);

  async function fetchTransactions() {
    try {
      setLoading(true);

      const filtersObject = {
        page: currentPage + 1,
        limit: perPage,
        sortField: sortBy?.id ?? 'id',
        sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
        search: searchTerm,
        userId,
        propertyId,
        isWithdrawalApproved: isApprovedFilterValue,
        ...filterObj,
      };

      switch (transactionType) {
        case 'primary':
          await dispatch(getPrimaryTransactions(filtersObject));
          break;

        case 'secondary':
          await dispatch(getSecondaryTransactions(filtersObject));
          break;

        case 'open':
          await dispatch(getPendingTransactions(filtersObject));
          break;

        case 'cancelled':
          await dispatch(getCancelledTransactions(filtersObject));
          break;

        case 'withdrawal':
          await dispatch(getWithdrawalTransactions(filtersObject));
          break;

        default:
          await dispatch(getPrimaryTransactions(filtersObject));
          break;
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onFetchDataDebounced = debounce(fetchTransactions, 0);

  useEffect(() => {
    setCurrentPage(0);
  }, [userId]);

  useEffect(() => {
    setCurrentPage(0);
  }, [propertyId]);

  useEffect(() => {
    setCurrentPage(0);
  }, [filterObj]);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [
    transactionType,
    currentPage,
    sortBy,
    perPage,
    userId,
    propertyId,
    filterObj.filterBy,
    filterObj.filterValues,
    searchTerm,
    isApprovedFilterValue
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetTransactions());
    };
  }, []);

  const columns = useMemo(() => {
    switch (transactionType) {
      case 'primary':
        return primaryColumns;

      case 'secondary':
        return secondaryColumns;

      case 'open':
        return pendingColumns;

      case 'cancelled':
        return cancelledColumns;

      case 'withdrawal':
        return withdrawalColumns;

      default:
        return [];
    }
  }, [transactionType]);

  const onCloseModal = () => {
    setIsApproveModal(false);
  }

  const onApproveClick = async () => {
    // setIsApproveModal(false);
    setIsApproveLoading(true)
    try {
      const response = await dispatch(
        approveTransaction({
          id: transactionId,
        })
      );
      toast.success(response?.payload?.data?.message);
    } catch (error) {
      console.log({ error });
    } finally {
      onCloseModal();
    }
    setIsApproveLoading(false)
  }

  const rows = useMemo(() => {
    switch (transactionType) {
      case 'primary':
        return transactionsList?.transactionsData?.length > 0
          ? transactionsList?.transactionsData?.map((transaction) => ({
            id: transaction?.transactionId ?? 'NA',
            transactionDate: transaction?.transactionDate ? formatDate(transaction?.transactionDate) : 'NA',
            northCapTradeId: transaction?.northCapTradeId ?? 'NA',
            propertyName: transaction?.propertyName ?? 'NA',
            propertyType: transaction?.propertyType ?? 'NA',
            userName: transaction?.userName ?? 'NA',
            transactionType: transaction?.transactionType ?? 'NA',
            noOfShares: transaction?.qty ?? 0,
            pricePerShare: (
              <TableTypography
                value={formatCurrency(transaction?.pricePerShare ?? 0)}
              />
            ),
            totalInvestmentAmount: (
              <TableTypography
                value={formatCurrency(transaction?.investmentAmount ?? 0)}
              />
            ),
            platformFees:transaction?.platformFees,
            equity: (
              <TableTypography
                value={formatPercentages(transaction?.equity ?? 0)}
              />
            ),
            fundedStatus: (
              <ProgressStatus value={transaction?.fundedStatus ?? 0} />
            ),
          }))
          : [];

      case 'secondary':
        return transactionsList?.transactionsData?.length > 0
          ? transactionsList?.transactionsData?.map((transaction) => ({
            id: transaction?.transactionId ?? 'NA',
            transactionDate: transaction?.transactionDate ? formatDate(transaction?.transactionDate) : 'NA',
            northCapAskOrderId: transaction?.northCapAskOrderId ?? 'NA',
            northCapBidOrderId: transaction?.northCapBidOrderId ?? 'NA',
            propertyName: transaction?.propertyName ?? 'NA',
            propertyType: transaction?.propertyType ?? 'NA',
            buyerName: transaction?.buyerName ?? 'NA',
            sellerName: transaction?.sellerName ?? 'NA',
            transactionType: transaction?.transactionType ?? 'NA',
            executedSharePrice: (
              <TableTypography
                value={formatCurrency(transaction?.executedSharePrice ?? 0)}
              />
            ),
            totalInvestmentAmount: (
              <TableTypography
                value={formatCurrency(transaction?.investmentAmount ?? 0)}
              />
            ),
            equity: (
              <TableTypography
                value={formatPercentages(transaction?.equity ?? 0)}
              />
            ),
          }))
          : [];

      case 'open':
        return transactionsList?.transactionsData?.length > 0
          ? transactionsList?.transactionsData?.map((transaction) => ({
            id: transaction?.orderId ?? 'NA',
            transactionDate: transaction?.transactionDate ? formatDate(transaction?.transactionDate) : 'NA',
            northCapTradeId: transaction?.northCapTradeId ?? 'NA',
            northCapOrderId: transaction?.northCapOrderId ?? 'NA',
            propertyName: transaction?.propertyName ?? 'NA',
            propertyType: transaction?.propertyType ?? 'NA',
            userName: transaction?.userName ?? 'NA',
            type: transaction?.transactionType ?? 'NA',
            noOfShares: transaction?.qty ?? 0,
            duration: transaction?.orderDuration ?? 'NA',
            pricePerShare: (
              <TableTypography
                value={formatCurrency(transaction?.pricePerShare ?? 0)}
              />
            ),
            totalInvestmentAmount: (
              <TableTypography
                value={formatCurrency(transaction?.investmentAmount ?? 0)}
              />
            ),
            fundedStatus: (
              <ProgressStatus value={transaction?.fundedStatus ?? 0} />
            ),
          }))
          : [];

      case 'cancelled':
        return transactionsList?.transactionsData?.length > 0
          ? transactionsList?.transactionsData?.map((transaction) => ({
            id: transaction?.orderId ?? 'NA',
            transactionDate: transaction?.transactionDate ? formatDate(transaction?.transactionDate) : 'NA',
            northCapTradeId: transaction?.northCapTradeId ?? 'NA',
            northCapOrderId: transaction?.northCapOrderId ?? 'NA',
            propertyName: transaction?.propertyName ?? 'NA',
            propertyType: transaction?.propertyType ?? 'NA',
            userName: transaction?.userName ?? 'NA',
            type: transaction?.transactionType ?? 'NA',
            noOfShares: transaction?.qty ?? 0,
            cancelledBy: transaction?.cancelledBy ?? 'NA',
            pricePerShare: (
              <TableTypography
                value={formatCurrency(transaction?.pricePerShare ?? 0)}
              />
            ),
            totalInvestmentAmount: (
              <TableTypography
                value={formatCurrency(transaction?.investmentAmount ?? 0)}
              />
            ),
          }))
          : [];

      case 'withdrawal':
        return transactionsList?.transactionsData?.length > 0
          ? transactionsList?.transactionsData?.map((transaction) => ({
            id: transaction?.userId ?? 'NA',
            transferId: transaction?.transferId ?? 'NA',
            userEmail: transaction?.userEmail ?? 'NA',
            source: transaction?.from ?? 'NA',
            destination: transaction?.to === 'NA' || transaction?.toLastFourDigits === 'NA' ? 'NA' : `${transaction?.to} ending with ${transaction?.toLastFourDigits}`,
            amount: `$${transaction?.amount}` ?? 'NA',
            transferDate: transaction?.transferDate ? formatDate(parseInt(transaction?.transferDate)) : 'NA',
            type: (
              <MDTypography
                fontSize="14px"
                color={!transaction.isWithdrawalApproved ? "dark" : "text"}
                sx={{ textDecoration: !transaction.isWithdrawalApproved && 'underline', cursor: !transaction.isWithdrawalApproved ? 'pointer' : 'not-allowed' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!transaction.isWithdrawalApproved) {
                    setIsApproveModal(true);
                    setTransactionId(transaction?.transferId);
                  }
                }}
              >
                {!transaction.isWithdrawalApproved ? 'Approve' : 'Approved'}
              </MDTypography>
            ),
          }))
          : [];

      default:
        return [];
    }
  }, [transactionType, transactionsList?.transactionsData]);

  const handleApproveStatus = (event) => {
    setCurrentPage(0);
    setIsApproveFilterValue(event?.target?.value?.trim())
  }

  const getTransactionName = () => {
    switch (transactionType) {
      case 'primary':
        return 'Primary';

      case 'secondary':
        return 'Secondary';

      case 'open':
        return 'Pending';

      case 'cancelled':
        return 'Cancelled';

      default:
        return 'Primary';
    }
  };

  const getDownloadTransactionRows = () => {
    switch (transactionType) {
      case 'primary':
        return primaryRows;

      case 'secondary':
        return secondaryRows;

      case 'open':
        return openRows;

      case 'cancelled':
        return cancelledRows;

      default:
        return primaryRows;
    }
  };

  const fetchDownloadTransactions = async () => {
    try {
      const response = await downloadTransactionService(
        getTransactionName(),
        sortBy?.id ?? 'id',
        sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
        '',
        userId,
        propertyId,
        filterObj.filterBy,
        filterObj.filterValues
      );

      let transactions = [];

      switch (transactionType) {
        case 'primary':
          transactions = response.data?.data?.primaryMarketTransactions;
          break;

        case 'secondary':
          transactions = response.data?.data?.secondaryMarketTransactions;
          break;

        case 'open':
          transactions = response.data?.data?.pendingOrders;
          break;

        case 'cancelled':
          transactions = response.data?.data?.cancelOrders;
          break;

        default:
          transactions = response.data?.data?.primaryMarketTransactions;
          break;
      }

      return transactions;
    } catch (error) {
      console.log(error);
      // return [];
      throw error;
    }
  };

  const onDownloadCSV = async () => {
    try {
      const transactions = await fetchDownloadTransactions();

      const csv = PapaParse.unparse(
        transactions.map(getDownloadTransactionRows()),
        {
          header: false,
        }
      );

      const headerParser = PapaParse.unparse(
        {
          fields: columns.map((col) => col.Header),
          data: [],
        },
        {
          header: true,
        }
      );

      const formattedCSVData = headerParser + csv;

      if (!transactions?.length) {
        toast.warn(
          `Unable to download file as there are no records to export!`
        );
        return;
      }

      await downloadCSV(
        formattedCSVData,
        `${getTransactionName()}-Transactions`
      );
    } catch (error) {
      toast.error(error?.message ?? 'Something went wrong!');
    }
  };

  const onDownloadXLSX = async () => {
    try {
      const transactions = await fetchDownloadTransactions();

      if (!transactions?.length) {
        toast.warn(
          `Unable to download file as there are no records to export!`
        );
        return;
      }

      await downloadXLSX(
        transactions.map(getDownloadTransactionRows()),
        columns.map((col) => col.Header),
        `${getTransactionName()}-Transactions`
      );
    } catch (error) {
      toast.error(error?.message ?? 'Something went wrong!');
    }
  };

  return {
    transactionsList,
    isLoading,
    rows,
    columns,
    totalCount: transactionsList?.totalCount,
    totalPage: transactionsList?.totalPage,
    perPage,
    setPerPage,
    currentPage,
    setCurrentPage,
    sortBy,
    setSortBy,
    userId,
    setUserId,
    propertyId,
    setPropertyId,
    filterObj,
    setFilterObj,
    onDownloadCSV,
    onDownloadXLSX,
    isApproveModal,
    setIsApproveModal,
    onCloseModal,
    onApproveClick,
    searchTerm,
    setSearchTerm,
    handleApproveStatus,
    isApprovedFilterValue,
    setIsApproveFilterValue,
    isApproveLoading
  };
};

export default useTransactionsHook;
